/* eslint-disable no-catch-shadow */
/* eslint-disable no-shadow */
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import React, {memo, useCallback, useState} from 'react';
import {useEffect} from 'react';
import {Platform, ScrollView, View} from 'react-native';
import {useToast} from 'react-native-fast-toast';
import { width, totalSize } from 'react-native-dimension';

import {StyleSheet} from 'react-native';
import {colorPalette} from '../../../core/config/color.config';
import {RNEText, SVGIcon, RNEInput, RNEButton} from '../../components';
import {
  useGetCondition,
  usePutCondition,
} from '../../server/react-query/useMedicalHistory';
import useStores from '../../stores/useStores';
import {formatDateString} from '../../utils/date.utils';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';

interface MedicalConditionViewProps {
  onCancel: () => void;
  onSuccess: () => void;
}

const MedicalConditionView = (props: MedicalConditionViewProps) => {
  const {patientProfileStore} = useStores();
  const navigation = useNavigation();
  const toast = useToast();
  const patientId = patientProfileStore.activeProfileData?.patientId!;
  const {status, data, error, isFetching} = useGetCondition({
    patientId: patientId,
  });

  const [value, setValue] = useState(
    data?.[0]?.note.text ? data?.[0]?.note.text : '',
  );
  const {setCanRefresh} = usePullDownRefreshContext();

  const mutation = usePutCondition([value], patientId);

  const onSave = async () => {
    try {
      await mutation.mutateAsync();

      mutation.reset();
      if (Platform.OS === 'web') {
        props.onSuccess();
      } else {
        toast?.show('Successfully updated the medical data');
        navigation.goBack();
      }
    } catch (error) {
      if (error?.data?.error) {
        toast?.show(error?.data?.error, {type: 'danger'});
      }
    }
  };

  useEffect(() => {
    setValue(data?.[0]?.note.text ? data?.[0]?.note.text : '');
  }, [data]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <View style={sharedStyles.root}>
      {Platform.OS === 'web' ? (
        <ScrollView>
          <View style={webStyles.mainContainer}>
            <View style={webStyles.topContainer}>
              <View style={webStyles.textContainer}>
                <View style={webStyles.titleContainer}>
                  <RNEText
                    label="Medical Condition"
                    color="accent"
                    variant="title"
                    weight="bold"
                  />
                  <View style={webStyles.spinnerContainer}>
                    <RNEButton
                      height={20}
                      loadingProps={loadingPropsStyle}
                      loading={isFetching}
                    />
                  </View>
                </View>
                <RNEText
                  label="Do you have any existing medical conditions? Please include anything congenital, genetic, physical, psychological in nature."
                  variant="body"
                  style={subtitleStyle}
                />
              </View>
              <View style={webStyles.iconContainer}>
                <SVGIcon name="hc-icon-medical-condition" size={60} />
              </View>
            </View>
            <View style={webStyles.bodyContainer}>
              <RNEInput
                height={200}
                variant="shadowed"
                value={value}
                onChangeText={text => setValue(text)}
                multiline={true}
                inputStyle={inputStyle}
                inputContainerStyle={inputContainerWebStyle}
              />
            </View>
            <View style={webStyles.footerContainer}>
              <View style={webStyles.dateContainer}>
                <RNEText
                  label={
                    data?.[0]?.modifiedOn
                      ? `Date Updated: ${formatDateString(
                          data?.[0]?.modifiedOn,
                        )}`
                      : ''
                  }
                  variant="button"
                />
              </View>
              <View style={webStyles.buttonsContainer}>
                <View style={webStyles.buttonContainer}>
                  <RNEButton
                    title="Cancel"
                    color="secondary"
                    onPress={props.onCancel}
                    style={webStyles.buttonStyle}
                    variant="outlined"
                  />
                </View>
                <RNEButton
                  title="Save"
                  color="secondary"
                  onPress={onSave}
                  style={webStyles.buttonStyle}
                  loading={mutation.isLoading}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <ScrollView
          onScroll={handleScroll}
          onLayout={e => {
            setScrollViewHeight(e.nativeEvent.layout.height);
          }}
          onContentSizeChange={(contentWidth, contentHeight) => {
            setContentHeight(contentHeight);
          }}>
          <View style={mobileStyles.mainContainer}>
            <View>
              <View style={mobileStyles.iconContainer}>
                <SVGIcon name="hc-icon-medical-condition" size={60} />
              </View>
              <View style={mobileStyles.titleContainer}>
                <RNEText
                  label="Medical Condition"
                  color="accent"
                  variant="title"
                  weight="bold"
                />
                <RNEButton
                  height={24}
                  loadingProps={loadingPropsStyle}
                  loading={isFetching}
                />
              </View>
              <RNEText
                label="Do you have any existing medical conditions? Please include anything congenital, genetic, physical, psychological in nature."
                variant="body"
              />
            </View>
            <View style={mobileStyles.bodyContainer}>
              <RNEInput
                height={240}
                value={value}
                onChangeText={text => setValue(text)}
                multiline={true}
                textAlignVertical="top"
                inputStyle={inputStyle}
                inputContainerStyle={inputContainerMobileStyle}
              />
            </View>
            <View style={mobileStyles.dateContainer}>
                <RNEText
                  label={
                    data?.[0]?.modifiedOn
                      ? `Date Updated: ${formatDateString(
                          data?.[0]?.modifiedOn,
                        )}`
                      : ''
                  }
                  variant="button"
                />
              </View>
            <View style={mobileStyles.footerContainer}>
              <View style={mobileStyles.buttonsContainer}>
                <View style={mobileStyles.buttonContainer}>
                  <RNEButton
                    title="Cancel"
                    color="secondary"
                    onPress={() => navigation.goBack()}
                    variant="outlined"
                  />
                </View>
                <View
                  style={[
                    mobileStyles.buttonContainer,
                    mobileStyles.buttonSibling,
                  ]}>
                  <RNEButton
                    title="Save"
                    color="secondary"
                    onPress={onSave}
                    loading={mutation.isLoading}
                  />
                </View>
              </View>
              
            </View>
          </View>
        </ScrollView>
      )}
    </View>
  );
};

const subtitleStyle = {
  width: '80%',
};

const loadingPropsStyle = {
  color: colorPalette.primary,
};

const inputContainerWebStyle = {
  padding: 20,
};

const inputContainerMobileStyle = {
  borderRadius: 12,
  paddingTop: 8,
  paddingBottom: 16,
};

const inputStyle = {
  lineHeight: 24,
};

const webStyles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    flex: 1,
  },
  topContainer: {
    flexDirection: 'row',
  },
  titleContainer: {
    flexDirection: 'row',
  },
  spinnerContainer: {
    alignSelf: 'flex-end',
    marginBottom: 8,
  },
  iconContainer: {
    justifyContent: 'flex-end',
    paddingRight: 20,
  },
  bodyContainer: {
    paddingVertical: 60,
    marginLeft: 8,
  },
  footerContainer: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dateContainer: {
    justifyContent: 'center',
  },
  buttonsContainer: {
    flexDirection: 'row',
  },
  buttonContainer: {
    marginRight: 16,
  },
  buttonStyle: {
    width: 120,
  },
  textContainer: {
    flex: 1,
  },
});

const mobileStyles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    flex: 1,
    padding: 52,
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconContainer: {
    paddingBottom: 12,
  },
  bodyContainer: {
    paddingVertical: totalSize(2),
  },
  footerContainer: {
    marginTop: 20,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  buttonContainer: {
    width: 100,
  },
  buttonSibling: {
    marginLeft: 16,
  },
  dateContainer: {
    marginTop: 0,
    marginBottom: totalSize(1),
  },
});

const sharedStyles = StyleSheet.create({
  root: {
    width: '100%',
    flexDirection: 'column',
  },
});

export default memo(MedicalConditionView);
